// 按人查看: alternate render of 待办总览's task list, grouped by assignee
// instead of by parent project. Same task set the project view shows, just
// pivoted — every task (待办 + every descendant) bucketed under its R.
//
// Lets Regina answer "what's on Cherry's plate this week" without scanning
// every project row. Click a task to jump to its detail.

import { useMemo, useState } from 'react';
import { Avatar } from './Avatar.jsx';
import { Prio, StatusPill } from './atoms.jsx';
import { isOverdue, fmtShort } from '../lib/dates.ts';

export const PersonPivotView = ({ tasks, staff, onOpen }) => {
  const [collapsed, setCollapsed] = useState(new Set());

  // Bucket tasks by primary R (assigneeId). Unassigned tasks go under
  // a synthetic "未分配" bucket so they don't quietly disappear.
  const groups = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      const key = t.assigneeId || '__unassigned__';
      const arr = map.get(key) ?? [];
      arr.push(t);
      map.set(key, arr);
    }
    return Array.from(map.entries())
      .map(([staffId, arr]) => ({
        staffId,
        staff: staffId === '__unassigned__' ? null
                                            : staff.find(s => s.id === staffId),
        tasks: arr.sort((a, b) => {
          // Surface overdue + not-done first, then by due date.
          const aDone = a.status === '已完成' || a.status === '已取消';
          const bDone = b.status === '已完成' || b.status === '已取消';
          if (aDone !== bDone) return aDone ? 1 : -1;
          return (a.dueDate || '9999').localeCompare(b.dueDate || '9999');
        })
      }))
      .sort((a, b) => {
        // Real people first; unassigned bucket last.
        if (!a.staff && b.staff) return 1;
        if (a.staff && !b.staff) return -1;
        return b.tasks.length - a.tasks.length;
      });
  }, [tasks, staff]);

  if (groups.length === 0) {
    return (
      <div className="vava-card empty-state">
        <div className="emoji">👤</div>
        <div className="title">这里还没有人接到待办</div>
      </div>
    );
  }

  function toggle(key) {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="person-pivot">
      {groups.map(g => {
        const key = g.staffId;
        const open = !collapsed.has(key);
        const done = g.tasks.filter(t => t.status === '已完成').length;
        const overdue = g.tasks.filter(isOverdue).length;
        const blocked = g.tasks.filter(t => t.status === '阻塞').length;
        return (
          <section key={key} className="person-pivot-group">
            <button type="button" className="person-pivot-head"
                    onClick={() => toggle(key)}
                    aria-expanded={open}>
              <span className="person-pivot-chevron" aria-hidden>{open ? '▾' : '▸'}</span>
              {g.staff ? (
                <>
                  <Avatar staff={g.staff} />
                  <span className="person-pivot-name">{g.staff.name}</span>
                  <span className="person-pivot-title">{g.staff.title || ''}</span>
                </>
              ) : (
                <span className="person-pivot-name person-pivot-unassigned">未分配</span>
              )}
              <span className="person-pivot-stats mono">
                {done}/{g.tasks.length} 完成
                {overdue > 0 && <span style={{ color: 'var(--vava-red)' }}> · {overdue} 逾期</span>}
                {blocked > 0 && <span style={{ color: 'var(--vava-red)' }}> · {blocked} 阻塞</span>}
              </span>
            </button>
            {open && (
              <table className="vava-table person-pivot-table">
                <thead>
                  <tr>
                    <th>待办</th>
                    <th style={{ width: 80 }}>优先级</th>
                    <th style={{ width: 100 }}>截止</th>
                    <th style={{ width: 100 }}>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {g.tasks.map(t => {
                    const over = isOverdue(t);
                    return (
                      <tr key={t.id} onClick={() => onOpen(t.id)}>
                        <td style={{ fontWeight: 500 }}>{t.title}</td>
                        <td><Prio value={t.priority}/></td>
                        <td className="mono"
                            style={{ color: over ? 'var(--vava-red)' : 'inherit',
                                     fontWeight: over ? 600 : 400 }}>
                          {fmtShort(t.dueDate)}{over && ' · 逾期'}
                        </td>
                        <td><StatusPill value={t.status}/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        );
      })}
    </div>
  );
};
